/**
 * authApi.ts - client API pour les endpoints auth locaux du tenant client.
 *
 * Base URL : /api (relatif - le SWA client est linke a sa FA via backend linking,
 * donc les requetes /api/* sont forwardees nativement avec les cookies).
 *
 * Toutes les requetes envoient credentials: 'include' pour propager le cookie
 * nis365_session HttpOnly Secure SameSite=Lax.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api';

export interface AuthUser {
  id:          string;
  email:       string;
  name:        string;
  firstName?:  string | null;
  lastName?:   string | null;
  phone?:      string | null;
  role:        'admin' | 'viewer' | string;
  /** ID du tenant NIS365 auquel l user appartient (archi B). */
  clientId:    string;
  hasPassword: boolean;
  lastLoginAt?: string | null;
}

interface ApiEnvelope<T> {
  data?: T;
  meta?: { requestId: string; ts: string };
  error?: { code: string; message: string; details?: unknown };
}

/** Etape 1 du login : OTP requis (code envoye par email). */
export interface LoginOtpRequired {
  step:          'otp_required';
  challengeId:   string;
  maskedEmail:   string;
  otpTtlSeconds: number;
  otpAttempts:   number;
}
/** Login termine : session posee (device reconnu ou apres OTP). */
export interface LoginCompleted extends AuthUser {
  step:              'completed';
  deviceRecognized?: boolean;
  deviceId?:         string;
  deviceExpiresAt?:  string;
}
export type LoginResult = LoginOtpRequired | LoginCompleted;

// --- Appareil de confiance : id memorise en localStorage (bypass OTP 30j) ---
const DEVICE_KEY = 'nis365_client_device';
export function getTrustedDeviceId(): string | null {
  try { return localStorage.getItem(DEVICE_KEY); } catch { return null; }
}
export function setTrustedDeviceId(id: string): void {
  try { localStorage.setItem(DEVICE_KEY, id); } catch { /* ignore */ }
}
export function clearTrustedDeviceId(): void {
  try { localStorage.removeItem(DEVICE_KEY); } catch { /* ignore */ }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
  const json = (await res.json().catch(() => ({}))) as ApiEnvelope<T>;
  if (!res.ok) {
    const msg = json.error?.message ?? `HTTP ${res.status}`;
    throw new Error(msg);
  }
  return (json.data ?? (json as unknown as T)) as T;
}

export const authApi = {
  /** Recupere le user authentifie (lit le cookie). 401 = pas connecte. */
  me: () => request<AuthUser>('/auth/me'),

  /** Met a jour le profil de l user courant (firstName, lastName, phone). */
  updateProfile: (body: { firstName?: string; lastName?: string; phone?: string }) =>
    request<AuthUser>('/auth/profile', {
      method: 'POST',
      body:   JSON.stringify(body),
    }),

  /** Demande un magic link. Reponse toujours generique (anti-enumeration). */
  requestMagicLink: (email: string) =>
    request<{ message: string }>('/auth/magic-link', {
      method: 'POST',
      body:   JSON.stringify({ email }),
    }),

  /** Verifie un magic link token, set le cookie de session. */
  verifyMagicLink: (token: string) =>
    request<AuthUser>('/auth/magic-link/verify', {
      method: 'POST',
      body:   JSON.stringify({ token }),
    }),

  /**
   * Login etape 1 : email + password. Retourne soit step='otp_required'
   * (code email a saisir), soit step='completed' (device de confiance reconnu).
   * rememberedDeviceId (localStorage) permet de sauter l OTP pendant 30j.
   */
  login: (email: string, password: string, rememberedDeviceId?: string | null) =>
    request<LoginResult>('/auth/login', {
      method: 'POST',
      body:   JSON.stringify({ email, password, rememberedDeviceId: rememberedDeviceId ?? undefined }),
    }),

  /** Login etape 2 : verifie le code OTP, pose la session. */
  verifyLoginOtp: (challengeId: string, code: string, rememberDevice: boolean) =>
    request<LoginCompleted>('/auth/login/verify-otp', {
      method: 'POST',
      body:   JSON.stringify({ challengeId, code, rememberDevice }),
    }),

  /** Demande un lien pour definir / reinitialiser le mot de passe. Reponse generique. */
  requestPasswordReset: (email: string) =>
    request<{ message: string }>('/auth/password-reset', {
      method: 'POST',
      body:   JSON.stringify({ email }),
    }),

  /** Consomme un token de reset et pose le nouveau mot de passe. */
  resetPassword: (token: string, newPassword: string) =>
    request<{ email: string; message: string }>('/auth/reset-password', {
      method: 'POST',
      body:   JSON.stringify({ token, newPassword }),
    }),

  /** Definit ou modifie le password. */
  setPassword: (newPassword: string, currentPassword?: string) =>
    request<{ message: string; hasPassword: boolean }>('/auth/set-password', {
      method: 'POST',
      body:   JSON.stringify({ newPassword, currentPassword }),
    }),

  /** Logout : clear le cookie. */
  logout: () =>
    request<{ message: string }>('/auth/logout', { method: 'POST' }),

  /**
   * Valide un token d invitation et bascule le user de pending → active.
   * Renvoie {alreadyActive, email, message}.
   * NB : endpoint public (pas besoin de session).
   */
  acceptInvitation: (token: string, newPassword?: string) =>
    request<{ alreadyActive: boolean; email: string; name?: string; message: string }>('/auth/accept-invitation', {
      method: 'POST',
      body:   JSON.stringify({ token, newPassword }),
    }),
};
